import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, Skeleton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import { useBackgroundImage } from "../../../components/FacilityManager/Hooks/useBackgroundImage";
import { DisplayViewport } from "../../../components/FacilityManager/Components/DisplayViewport/DisplayViewport";
import { FloorPlanBackground } from "../../../components/FacilityManager/Components/FloorPlanBackground/FloorPlanBackground";
import { PlaceContainer } from "../../../components/FacilityManager/Components/Place/PlaceContainer";
import { ZoneContainer } from "../../../components/FacilityManager/Components/Zone/ZoneContainer";
import { EmptyBackgroundText } from "../../../components/FacilityManager/Components/Canvas/Partials/EmptyBackgroundText.partial";
import { PlanViewMode } from "../../../components/FacilityManager/Components/PixiViewport/PixiViewport";
import { FloorZoomMode } from "../../../components/FacilityManager/Domain/Types/FloorZoomMode.type";
import { handleZoomToHighlight } from "../../../components/FacilityManager/Components/Views/DisplayFloorplanView/DisplayFloorPlanView.functions";
import JumpIcon from "../../../components/FacilityManager/Components/Canvas/Partials/jump-icon.component";
import type { IViewport } from "../../../components/FacilityManager/Domain/Types/FloorPlan/Viewport.type";
import {
  generateBookingPlaceVariant,
  generateZoneVariant
} from "../../../components/FacilityManager/Functions/generateVariant";
import {
  getFloorPlanCanvasCorner,
  resolveFloorPlanCanvasSize,
  resolveFloorPlanPlaceScale
} from "../../../components/FacilityManager/Functions/floorPlanCanvas.functions";
import { useGeometry } from "../../../components/FacilityManager/Hooks/useGeometry";
import { useMousePosition } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/useMousePosition";
import { getFavoriteInventoryIdsSetForMode } from "../../../components/BookingForm/functions/form-workplace-favorites.functions";
import { useRemoteFetchBookingFavoritesByFloor } from "../../../hooks/Remote/BookingFavorites/useRemoteFetchBookingFavoritesByFloor";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import type { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import { SelectionPrompt2 } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/SelectionPrompt(2)";
import {
  buildColleagueDeskTooltipLines,
  buildColleagueWorkplaceSeatStatus,
  colleagueFloorBookConfirmCopy,
  colleagueFloorBookedObjectPosition,
  colleagueFloorPlaceBookingType,
  colleagueFloorPlaceClickKind,
  colleagueFloorZoneClickKind,
  colleagueFloorSceneKey,
  generateColleagueFloorIcon,
  isColleagueFloorOccupantPlaceType,
  isColleagueUserOccupiedDesk,
  occupancyBookingsForDesk,
  toDrawableColleagueFloorIcon,
  toHighlightedColleagueUserBookings
} from "./colleague-workplace-floor.functions";
import { ColleagueDeskClickKind } from "./colleague-workplace-floor.types";
import { ColleagueWorkplaceFloorDeskTooltip } from "./colleague-workplace-floor-desk-tooltip.partial";

function outlineImagePath(outlineUrl: string | undefined): string {
  return outlineUrl?.split("?")[0] ?? "";
}

/** Gray stand-in shown until the next floor image has been drawn. */
function ColleagueFloorPlanPlaceholder({ overlay = false }: { overlay?: boolean }) {
  return (
    <Skeleton
      variant="rectangular"
      data-testid="colleague-workplace-floor-loading"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        ...(overlay ? { position: "absolute", inset: 0, zIndex: 1 } : {})
      }}
    />
  );
}

type P = {
  floorPlan?: IFloorPayload;
  currentFloor?: FloorAvailability;
  isLoading: boolean;
  highlightInventoryId?: number;
  /** User's workplace, parking, and charging bookings for the open day. Icon only. */
  occupantInventoryIds?: readonly number[];
  /** Those bookings' own hours. Hover uses them instead of Von and Bis. */
  ownOccupancyBookings?: ReadonlyArray<{
    bookingInventoryId: number;
    bookingId?: number;
    start: string;
    end: string;
  }>;
  searchedColleagues: ConnectedBookingData[];
  bookingInputs: BookingInputs;
  timeFormat: string;
  /** Favorites to mark. Workplace unless the plan was opened from parking or charging. */
  favoriteBookingType?: BookingType;
  /** Workplace unless the plan was opened for parking, charging, or conference. */
  availabilityBookingType?: BookingType;
  onConfirmInventoryId: (inventoryId: number, placeTypeId?: number) => void;
  onOpenConferenceRoom?: (inventoryId: number) => void;
  onOpenSessionTimeEditor: () => void;
  onOccupancyInventoryId: (inventoryId: number) => void;
};

export function ColleagueWorkplaceFloorCanvas({
  floorPlan,
  currentFloor,
  isLoading,
  highlightInventoryId,
  occupantInventoryIds,
  ownOccupancyBookings,
  searchedColleagues,
  bookingInputs,
  timeFormat,
  favoriteBookingType = BookingType.WORKPLACE,
  availabilityBookingType,
  onConfirmInventoryId,
  onOpenConferenceRoom,
  onOpenSessionTimeEditor,
  onOccupancyInventoryId
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userInformation } = useSelector((state: RootState) => state.login);
  const { convertPixiPoints } = useGeometry();
  const [mouseX, mouseY] = useMousePosition();
  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [activeTouchCount, setActiveTouchCount] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState<number | undefined>();
  const [hoveredInventoryId, setHoveredInventoryId] = useState<number | undefined>();
  const [floorZoomMode, setFloorZoomMode] = useState(FloorZoomMode.INITIAL);
  const [floorRotation, setFloorRotation] = useState(0);
  const { data: floorFavoritesData } = useRemoteFetchBookingFavoritesByFloor(
    floorPlan?.floorInventoryId
  );
  const favoriteInventoryIds = useMemo(
    () => getFavoriteInventoryIdsSetForMode(floorFavoritesData?.favorites, favoriteBookingType),
    [favoriteBookingType, floorFavoritesData?.favorites]
  );
  const ownOccupantInventoryIds = useMemo(
    () => new Set(occupantInventoryIds ?? []),
    [occupantInventoryIds]
  );
  const sceneKey = colleagueFloorSceneKey(floorPlan);
  const outlinePath = outlineImagePath(floorPlan?.outlineUrl);
  const outlinePathRef = useRef(outlinePath);
  outlinePathRef.current = outlinePath;
  const [drawnOutlinePath, setDrawnOutlinePath] = useState<string | null>(null);
  const [revealedSceneKey, setRevealedSceneKey] = useState("");

  const { background, isEmptyBgUsed } = useBackgroundImage({
    backgroundImageUrl: floorPlan?.outlineUrl,
    viewport: floorPlan?.viewport
  });

  const bookedObjectPosition = useMemo(
    () => colleagueFloorBookedObjectPosition(floorPlan, highlightInventoryId),
    [floorPlan, highlightInventoryId]
  );

  useEffect(() => {
    setSelectedPlace(undefined);
    setHoveredInventoryId(undefined);
    setFloorZoomMode(FloorZoomMode.INITIAL);
    setFloorRotation(0);
  }, [sceneKey]);

  useEffect(() => {
    if (!background) {
      return;
    }
    setDrawnOutlinePath(outlinePathRef.current);
  }, [background]);

  const canvasSize = useMemo(() => {
    if (!background || !floorPlan) {
      return undefined;
    }
    return resolveFloorPlanCanvasSize(floorPlan, background);
  }, [background, floorPlan]);
  const placeScale = useMemo(() => {
    if (!background || !floorPlan) {
      return 0;
    }
    return resolveFloorPlanPlaceScale(floorPlan, background);
  }, [background, floorPlan]);

  const seatStatus = useMemo(
    () =>
      currentFloor
        ? buildColleagueWorkplaceSeatStatus(currentFloor)
        : {
            availableList: [],
            occupiedList: [],
            disableList: [],
            restrictedList: [],
            userOccupiedList: [],
            partialList: []
          },
    [currentFloor]
  );

  const searchedIds = useMemo(
    () => new Set(searchedColleagues.map(row => row.bookingInventoryId)),
    [searchedColleagues]
  );
  const highlightedUserBookings = useMemo(
    () => toHighlightedColleagueUserBookings(bookingInputs.usersBookedFor, highlightInventoryId),
    [bookingInputs.usersBookedFor, highlightInventoryId]
  );
  const selectedPlaceEntry = useMemo(
    () => floorPlan?.places.find(place => place.id === selectedPlace),
    [floorPlan?.places, selectedPlace]
  );
  const currentUserForOccupancy = useMemo(
    () => ({
      userId: userInformation.sub,
      firstName: userInformation.firstName,
      lastName: userInformation.surname,
      email: userInformation.email
    }),
    [userInformation.email, userInformation.firstName, userInformation.sub, userInformation.surname]
  );
  const hoverTooltipLines = useMemo(() => {
    if (hoveredInventoryId == null || selectedPlace != null) {
      return [];
    }
    const bookings = occupancyBookingsForDesk({
      inventoryId: hoveredInventoryId,
      connectedBookingData: currentFloor?.connectedBookingData ?? [],
      currentUser: currentUserForOccupancy,
      isUserOccupied: isColleagueUserOccupiedDesk(hoveredInventoryId, seatStatus),
      ownBookings: ownOccupancyBookings
    });
    return buildColleagueDeskTooltipLines(
      bookings,
      timeFormat,
      t("_colleagueFloor_bookingTimeUnknown")
    );
  }, [
    currentFloor?.connectedBookingData,
    ownOccupancyBookings,
    currentUserForOccupancy,
    hoveredInventoryId,
    seatStatus,
    selectedPlace,
    t,
    timeFormat
  ]);
  const outlineReady = outlinePath === drawnOutlinePath;
  const isViewportReady =
    outlineReady && Boolean(floorPlan && currentFloor && background && placeScale && canvasSize);
  const waitingForOutline = Boolean(floorPlan?.outlineUrl) && !outlineReady;
  const revealImmediately = process.env.NODE_ENV === "test";

  useEffect(() => {
    if (!isViewportReady) {
      return;
    }
    if (revealImmediately) {
      setRevealedSceneKey(sceneKey);
      return;
    }
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setRevealedSceneKey(sceneKey));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [isViewportReady, revealImmediately, sceneKey]);

  const planVisible = isViewportReady && revealedSceneKey === sceneKey;

  if (!isViewportReady && (isLoading || waitingForOutline)) {
    return <ColleagueFloorPlanPlaceholder />;
  }

  if (!floorPlan || !currentFloor || !background || !placeScale || !canvasSize) {
    return (
      <Box
        data-testid="colleague-workplace-floor-empty"
        sx={{ width: "100%", height: "100%", minHeight: 0 }}
      />
    );
  }

  return (
    <Box
      data-testid="colleague-workplace-floor-plan"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        flex: 1,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          visibility: planVisible ? "visible" : "hidden"
        }}
      >
        <DisplayViewport
          key={sceneKey}
          planViewMode={PlanViewMode.BOOKING}
          floorPlanName="colleague-workplace"
          initialZoomEnd={getFloorPlanCanvasCorner(canvasSize)}
          currentFloorPlan={floorPlan}
          placeScale={placeScale}
          viewportDimensions={viewportDimensions}
          setViewportDimensions={setViewportDimensions}
          setActiveTouchCount={setActiveTouchCount}
          initialPos={bookedObjectPosition}
          floorZoomMode={bookedObjectPosition != null ? floorZoomMode : undefined}
          floorRotation={floorRotation}
        >
          {!isEmptyBgUsed && <FloorPlanBackground texture={background} floorPlan={floorPlan} />}
          {isEmptyBgUsed && (
            <EmptyBackgroundText
              rectWidth={floorPlan.viewport.width}
              rectHeight={floorPlan.viewport.height}
              posY={5}
            />
          )}
          {floorPlan.zones.map(zone => {
            const inventoryId = zone.inventory?.id ?? zone.inventoryId;
            const zoneTypeId = zone.inventory?.zoneTypeId ?? zone.zoneTypeId;
            const clickKind = colleagueFloorZoneClickKind(
              zoneTypeId,
              inventoryId,
              seatStatus,
              availabilityBookingType
            );
            const isClickable = clickKind !== ColleagueDeskClickKind.NONE;
            const zoneBookings =
              inventoryId == null
                ? []
                : occupancyBookingsForDesk({
                    inventoryId,
                    connectedBookingData: currentFloor.connectedBookingData ?? [],
                    currentUser: currentUserForOccupancy,
                    isUserOccupied: isColleagueUserOccupiedDesk(inventoryId, seatStatus)
                  });
            return (
              <ZoneContainer
                id={zone.id}
                key={zone.id}
                inventoryId={inventoryId}
                walls={convertPixiPoints(zone.coordinates)}
                disabled={zone.disabled}
                zoneType={zoneTypeId ?? 0}
                onClick={() => {
                  if (!isClickable || inventoryId == null) {
                    return;
                  }
                  if (clickKind === ColleagueDeskClickKind.BOOK) {
                    onOpenConferenceRoom?.(inventoryId);
                    return;
                  }
                  onOccupancyInventoryId(inventoryId);
                }}
                onPointerEnter={() => {
                  if (zoneBookings.length > 0 && inventoryId != null) {
                    setHoveredInventoryId(inventoryId);
                  }
                }}
                onPointerLeave={() => {
                  setHoveredInventoryId(prev => (prev === inventoryId ? undefined : prev));
                }}
                isClickable={isClickable}
                isFavorite={
                  inventoryId != null && (favoriteInventoryIds?.has(inventoryId) ?? false)
                }
                isSelected={false}
                showWarning={false}
                interactiveConferenceWarning={false}
                setIsHoverWarning={() => undefined}
                activeTouchCount={activeTouchCount}
                warnScale={1}
                variant={generateZoneVariant(zone, seatStatus)}
              />
            );
          })}
          {floorPlan.places.map(workplace => {
            const inventoryId = workplace.inventoryId || workplace.inventory?.id;
            const placeTypeId = workplace.placeTypeId || workplace.inventory?.placeTypeId;
            const clickKind = colleagueFloorPlaceClickKind(placeTypeId, inventoryId, seatStatus);
            const isClickable = clickKind !== ColleagueDeskClickKind.NONE;
            const isColleaguesPlace = inventoryId != null && searchedIds.has(inventoryId);
            const isUserOccupiedPlace =
              isColleagueUserOccupiedDesk(inventoryId, seatStatus) ||
              (inventoryId != null &&
                isColleagueFloorOccupantPlaceType(placeTypeId) &&
                ownOccupantInventoryIds.has(inventoryId));
            const deskBookings =
              isColleagueFloorOccupantPlaceType(placeTypeId) && inventoryId != null
                ? occupancyBookingsForDesk({
                    inventoryId,
                    connectedBookingData: currentFloor.connectedBookingData ?? [],
                    currentUser: currentUserForOccupancy,
                    isUserOccupied: isUserOccupiedPlace
                  })
                : [];
            const hasOccupancyTooltip = deskBookings.length > 0;
            const acceptsPointer = isClickable || hasOccupancyTooltip;
            return (
              <PlaceContainer
                key={workplace.id}
                boundingBox={{
                  height: workplace.inventory?.boundingBox?.height ?? 80,
                  width: workplace.inventory?.boundingBox?.width ?? 160
                }}
                inventoryId={inventoryId}
                id={workplace.id}
                position={workplace.position}
                rotation={workplace.rotate}
                isHighlighted={inventoryId === highlightInventoryId}
                isFavorite={
                  inventoryId != null && (favoriteInventoryIds?.has(inventoryId) ?? false)
                }
                isSelected={workplace.id === selectedPlace}
                onClick={() => {
                  if (!isClickable || inventoryId == null) {
                    return;
                  }
                  if (clickKind === ColleagueDeskClickKind.BOOK) {
                    setSelectedPlace(workplace.id);
                    return;
                  }
                  onOccupancyInventoryId(inventoryId);
                }}
                onPointerEnter={() => {
                  if (hasOccupancyTooltip && inventoryId != null) {
                    setHoveredInventoryId(inventoryId);
                  }
                }}
                onPointerLeave={() => {
                  setHoveredInventoryId(prev => (prev === inventoryId ? undefined : prev));
                }}
                isSelectable={isClickable}
                eventMode={acceptsPointer ? "static" : "none"}
                variant={generateBookingPlaceVariant(
                  { workplace, zone: undefined },
                  seatStatus,
                  colleagueFloorPlaceBookingType(placeTypeId)
                )}
                icon={toDrawableColleagueFloorIcon(
                  generateColleagueFloorIcon({
                    highlightedUserBookings,
                    userInformation,
                    searchedColleagues,
                    inventoryId,
                    isUserOccupiedPlace
                  }),
                  userInformation
                )}
                isColleaguesPlace={isColleaguesPlace}
                placeTypeId={placeTypeId}
                placeScale={placeScale}
                activeTouchCount={activeTouchCount}
                tool="SELECT.BOOK"
              />
            );
          })}
        </DisplayViewport>
      </Box>
      <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 2 }}>
        <JumpIcon
          data-testid="zoom-to-highlight-btn"
          brandColor={theme.palette.primary.main}
          toggleZoomHighlight={floorZoomMode !== FloorZoomMode.ZOOMIN}
          ariaLabel={
            floorZoomMode === FloorZoomMode.ZOOMIN
              ? t("_colleagueFloor_zoomOutFloor")
              : t("_colleagueFloor_zoomToBooking")
          }
          onClick={event => {
            event.preventDefault();
            if (bookedObjectPosition == null) {
              return;
            }
            handleZoomToHighlight(floorZoomMode, setFloorZoomMode, setFloorRotation);
          }}
        />
      </Box>
      {!planVisible && <ColleagueFloorPlanPlaceholder overlay />}
      <ColleagueWorkplaceFloorDeskTooltip
        lines={hoverTooltipLines}
        mouseX={mouseX}
        mouseY={mouseY}
      />
      {selectedPlaceEntry && (
        <Dialog open onClose={() => setSelectedPlace(undefined)}>
          <SelectionPrompt2
            selected={{ place: selectedPlaceEntry, zone: undefined }}
            selectableByVariant={true}
            bookingInputs={bookingInputs}
            floorPlanForService={undefined}
            floorInventoryId={floorPlan.floorInventoryId}
            userIndex={0}
            userId={userInformation.sub}
            isOccupiedDataLoading={false}
            onConfirm={() => {
              const inventoryId =
                selectedPlaceEntry.inventoryId ?? selectedPlaceEntry.inventory?.id;
              if (inventoryId != null) {
                onConfirmInventoryId(
                  inventoryId,
                  selectedPlaceEntry.placeTypeId ?? selectedPlaceEntry.inventory?.placeTypeId
                );
              }
              setSelectedPlace(undefined);
            }}
            onClose={event => {
              event.stopPropagation();
              setSelectedPlace(undefined);
            }}
            confirmButtonLabel={t(
              colleagueFloorBookConfirmCopy(
                selectedPlaceEntry.placeTypeId ?? selectedPlaceEntry.inventory?.placeTypeId
              ).confirmKey
            )}
            openTimeDialog={() => {
              setSelectedPlace(undefined);
              onOpenSessionTimeEditor();
            }}
          />
        </Dialog>
      )}
    </Box>
  );
}
